import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, X } from "@phosphor-icons/react";

export default function PbxUnrecognized({ orgId }) {
  const qc = useQueryClient();
  const [assign, setAssign] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["pbx-unrecognized", orgId],
    queryFn: async () => (await api.get("/pbx/unrecognized", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  const items = data?.items || [];

  const dismiss = useMutation({
    mutationFn: async (id) => (await api.post(`/pbx/unrecognized/${id}/dismiss`)).data,
    onSuccess: () => { toast("Dismissed"); qc.invalidateQueries({ queryKey: ["pbx-unrecognized"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="space-y-4" data-testid="pbx-unrecognized">
      <p className="text-sm text-muted-foreground">Requests the SI couldn’t confidently route. Assign them to an intent to teach your SI — nothing changes automatically without your approval.</p>
      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-card p-10 text-center">
          <Sparkle size={24} className="mx-auto text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">Nothing to review</p>
          <p className="mt-1 text-xs text-muted-foreground">Unclassified caller requests will appear here for you to train.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} data-testid={`unrec-${it.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-card px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm">“{it.transcript}”</p>
                <p className="text-xs text-muted-foreground">{it.caller || "unknown"} · {(it.at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button data-testid={`unrec-assign-${it.id}`} onClick={() => setAssign(it)}
                  className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground">Assign to intent</button>
                <button data-testid={`unrec-dismiss-${it.id}`} onClick={() => dismiss.mutate(it.id)}
                  className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {assign && (
        <AssignModal item={assign} orgId={orgId}
          onClose={() => setAssign(null)}
          onDone={() => { setAssign(null); qc.invalidateQueries({ queryKey: ["pbx-unrecognized"] }); qc.invalidateQueries({ queryKey: ["pbx-intents"] }); }} />
      )}
    </div>
  );
}

function AssignModal({ item, orgId, onClose, onDone }) {
  const [mode, setMode] = useState("existing");
  const [intentId, setIntentId] = useState("");
  const [name, setName] = useState("");
  const [dest, setDest] = useState("");

  const { data: intentsData } = useQuery({
    queryKey: ["pbx-intents", orgId],
    queryFn: async () => (await api.get("/pbx/intents", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  const { data: extData } = useQuery({
    queryKey: ["pbx-extensions", orgId],
    queryFn: async () => (await api.get("/pbx/extensions", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  const intents = intentsData?.intents || [];
  const exts = extData?.extensions || [];

  const submit = useMutation({
    mutationFn: async () => {
      const body = mode === "existing" ? { intent_id: intentId } : { name, destination_ext: dest };
      return (await api.post(`/pbx/unrecognized/${item.id}/assign`, body)).data;
    },
    onSuccess: (d) => { toast.success(`Added to “${d.assigned_intent}”`); onDone(); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="unrec-assign-modal">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-card p-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Assign to intent</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <p className="rounded-lg bg-secondary px-3 py-2 text-sm">“{item.transcript}”</p>
        <div className="mt-3 flex gap-2 text-xs">
          <button onClick={() => setMode("existing")} className={`rounded-full px-3 py-1.5 font-semibold ${mode === "existing" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>Existing intent</button>
          <button onClick={() => setMode("new")} className={`rounded-full px-3 py-1.5 font-semibold ${mode === "new" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>New intent</button>
        </div>
        {mode === "existing" ? (
          <select data-testid="assign-intent-select" value={intentId} onChange={(e) => setIntentId(e.target.value)}
            className="mt-3 w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm">
            <option value="">Choose an intent…</option>
            {intents.map((i) => <option key={i.id} value={i.id}>{i.name} → {i.destination_ext}</option>)}
          </select>
        ) : (
          <div className="mt-3 space-y-2">
            <input data-testid="assign-new-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="New intent name" className="w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm" />
            <select data-testid="assign-new-dest" value={dest} onChange={(e) => setDest(e.target.value)}
              className="w-full rounded-lg border border-white/10 bg-background px-3 py-2 text-sm">
              <option value="">Route to extension…</option>
              {exts.map((e) => <option key={e.ext} value={e.ext}>{e.ext} — {e.name}</option>)}
            </select>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-muted-foreground">Cancel</button>
          <button data-testid="assign-submit" onClick={() => submit.mutate()}
            disabled={submit.isPending || (mode === "existing" ? !intentId : !name.trim() || !dest)}
            className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
            {submit.isPending ? "Saving…" : "Assign"}</button>
        </div>
      </div>
    </div>
  );
}
